import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Image as ImageIcon, Upload, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../../../lib/supabase';
import { Database } from '../../../../lib/database.types';

type Business = Database['public']['Tables']['businesses']['Row'];

interface BusinessLogoProps {
  business: Pick<Business, 'id' | 'logo_url'> & Partial<Business>;
  onLogoChange: (logoPath: string) => Promise<void>;
}

const BUCKET = 'business_assets';
const SIGNED_TTL_SECONDS = 60 * 60 * 24 * 7; // 7 ימים

const dbg = (...args: any[]) => console.log('[LOGO][BusinessLogo]', ...args);
const warn = (...args: any[]) => console.warn('[LOGO][BusinessLogo]', ...args);

async function resolveLogoSrc(pathOrUrl: string | null): Promise<string | null> {
  if (!pathOrUrl) return null;
  if (pathOrUrl.startsWith('http')) {
    dbg('resolve: got http url -> return as is');
    return pathOrUrl;
  }

  try {
    const { data: signed, error } = await supabase
      .storage
      .from(BUCKET)
      .createSignedUrl(pathOrUrl, SIGNED_TTL_SECONDS);

    if (!error && signed?.signedUrl) {
      dbg('resolve: signed url generated');
      return signed.signedUrl;
    }
    const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(pathOrUrl);
    dbg('resolve: fallback publicUrl:', !!pub?.publicUrl);
    return pub?.publicUrl ?? null;
  } catch (e) {
    warn('resolve failed:', e);
    return null;
  }
}

function isInternalPath(val?: string | null): val is string {
  return !!val && !val.startsWith('http');
}

function makeDestPath(businessId: string, fileName: string) {
  const ext = (fileName.split('.').pop() || 'png').toLowerCase();
  return `${businessId}/logo-${Date.now()}.${ext}`;
}

export function BusinessLogo({ business, onLogoChange }: BusinessLogoProps) {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      dbg('mount/update: business.logo_url =', business?.logo_url);
      const url = await resolveLogoSrc(business?.logo_url ?? null);
      if (!mounted) return;
      const finalUrl = url ? `${url}${url.includes('?') ? '&' : '?'}v=${Date.now()}` : null;
      dbg('resolved preview url?', !!finalUrl);
      setPreviewUrl(finalUrl);
    })();
    return () => { mounted = false; };
  }, [business?.logo_url]);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!business?.id) {
      toast.error('חסרים פרטי עסק');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('גודל הקובץ חייב להיות עד 5MB');
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error('ניתן להעלות רק קבצי תמונה');
      return;
    }

    try {
      setUploading(true);

      if (isInternalPath(business.logo_url)) {
        dbg('removing previous internal logo:', business.logo_url);
        const { error: rmErr } = await supabase.storage.from(BUCKET).remove([business.logo_url]);
        if (rmErr) warn('remove previous logo failed (non-fatal):', rmErr);
      }

      const destPath = makeDestPath(business.id, file.name);
      dbg('uploading to:', destPath);

      const { error: uploadError } = await supabase
        .storage
        .from(BUCKET)
        .upload(destPath, file, { upsert: true });
      if (uploadError) throw uploadError;

      dbg('upload ok, updating DB via onLogoChange…');
      await onLogoChange(destPath);

      const fresh = await resolveLogoSrc(destPath);
      const final = fresh ? `${fresh}${fresh.includes('?') ? '&' : '?'}v=${Date.now()}` : null;
      dbg('fresh preview set?', !!final);
      setPreviewUrl(final);

      toast.success('הלוגו הועלה בהצלחה');
    } catch (err: any) {
      warn('upload error:', err);
      toast.error(err?.message || 'שגיאה בהעלאת הלוגו');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    if (!business?.id || !business.logo_url) return;
    try {
      setUploading(true);

      if (isInternalPath(business.logo_url)) {
        dbg('removing current internal logo:', business.logo_url);
        const { error: rmErr } = await supabase.storage.from(BUCKET).remove([business.logo_url]);
        if (rmErr) warn('remove error (non-fatal):', rmErr);
      }

      await onLogoChange('');
      setPreviewUrl(null);
      toast.success('הלוגו הוסר בהצלחה');
    } catch (err: any) {
      warn('remove error:', err);
      toast.error(err?.message || 'שגיאה בהסרת הלוגו');
    } finally {
      setUploading(false);
    }
  };

  const onDrop: React.DragEventHandler<HTMLLabelElement> = (ev) => {
    ev.preventDefault();
    const file = ev.dataTransfer.files?.[0];
    if (!fileInputRef.current || !file) return;
    const dt = new DataTransfer();
    dt.items.add(file);
    fileInputRef.current.files = dt.files;
    fileInputRef.current.dispatchEvent(new Event('change', { bubbles: true }));
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        <div className="flex items-center gap-2">
          <ImageIcon className="h-4 w-4" />
          <span>לוגו העסק</span>
        </div>
      </label>

      <div className="bg-gray-50 p-4 rounded-xl">
        {previewUrl ? (
          <div className="relative inline-block">
            <img
              src={previewUrl}
              alt="לוגו העסק"
              className="h-32 w-32 object-cover rounded-xl"
              onError={async () => {
                warn('img onError -> re-signing url…');
                const fresh = await resolveLogoSrc(business?.logo_url ?? null);
                setPreviewUrl(fresh ? `${fresh}${fresh.includes('?') ? '&' : '?'}v=${Date.now()}` : null);
              }}
            />
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleRemoveLogo}
              disabled={uploading}
              className="absolute -top-2 -right-2 p-1 bg-red-100 text-red-600 rounded-full hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="הסר לוגו"
              title="הסר לוגו"
            >
              <X className="h-4 w-4" />
            </motion.button>
          </div>
        ) : (
          <label
            className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-xl hover:border-indigo-500 transition-colors cursor-pointer"
            htmlFor="business-logo-input"
            tabIndex={0}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
          >
            <div className="space-y-1 text-center w-full">
              <Upload className="mx-auto h-12 w-12 text-gray-400" />
              <div className="flex text-sm text-gray-600 justify-center">
                <span className="relative cursor-pointer rounded-md bg-white font-medium text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 hover:text-indigo-500">
                  העלה תמונה
                </span>
                <p className="pr-1">או גרור לכאן</p>
              </div>
              <p className="text-xs text-gray-500">PNG, JPG עד 5MB</p>

              <input
                ref={fileInputRef}
                id="business-logo-input"
                type="file"
                className="sr-only"
                accept="image/*"
                onChange={handleImageChange}
                disabled={uploading}
              />
            </div>
          </label>
        )}
      </div>
    </div>
  );
}

export default BusinessLogo;
